import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import verified from '../assets/images/verified.svg';
import commentsend from '../assets/images/comment_send.svg';
import drop_horz from '../assets/images/drop_horz.svg';
import TipButton from './TipButton';
import config from '../config/config';

const PostMeta = ({ 
  heart_icon,
  heart_icon_fill,
  comment_icon,
  tip_icon,
  bookmark_icon,
  bookmark_icon_fill,
  postId,
  userId, 
  walletBalance,
}) => {
  // Core state
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [media, setMedia] = useState([]);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [mediaType, setMediaType] = useState('');
  const [commentsVisible, setCommentsVisible] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsCount, setCommentsCount] = useState(0);
  const [commentLikes, setCommentLikes] = useState({});
  const [userDetails, setUserDetails] = useState(null);
  const [replyToCommentId, setReplyToCommentId] = useState(null);
  const [userName, setUserName] = useState('');
  const [comment, setComment] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [replyToUser, setReplyToUser] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [post, setPost] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mention-related state
  const [mentions, setMentions] = useState([]);
  const [mentionList, setMentionList] = useState([]);
  const [showMentionList, setShowMentionList] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const textareaRef = useRef(null);
  
  // Common emojis
  const commonEmojis = ['😊', '😂', '😍', '🥺', '😎', '😢', '😠', '😋', '🙌', '😁', '😉', '👍', '🙏', '🥳'];
  
  // Display comments
  const displayedComments = showMore ? comments : comments.slice(0, 3);

  // Fetch all users for mentions
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await axios.get(`${config.baseURL}/get-mentions-creators`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (response.data.data) {
          const users = response.data.data.map(item => ({
            id: item.user.id,
            user_name: item.user.user_name,
            name: item.user.name,
            profile: item.user.profile,
          }));
          setAllUsers(users);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchAllUsers();
  }, []);

  // Fetch post data
  useEffect(() => {
    fetchPostData();
  }, [postId]);

  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await axios.get(`${config.baseURL}/current-user-details`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const userDetailsData = response.data.data.user_details;
        const userNameData = response.data.data.user.user_name;

        setUserDetails(userDetailsData);
        setUserName(userNameData);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    fetchUserProfile();
  }, []);

  const fetchPostData = async () => {
    if (!postId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.baseURL}/get-posts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const post = response.data.posts.find(post => post.id === postId);

      if (post) {
        setPost(post);
        setIsLiked(post.liked_status === 'active');
        setLikesCount(post.total_likes);
        setIsBookmarked(post.bookmark_status === 'active');
        setMediaType(post.media.length ? post.media[0].url : '');
        setComments(post.comments || []);
        setCommentsCount(post.comments_count?.aggregate || 0);
        setIsSubscribed(post.is_subscribed);

        // Initialize comment likes status
        const initialCommentLikes = {};
        post.comments.forEach(comment => {
          initialCommentLikes[comment.id] = {
            isLiked: comment.liked_status === 'active',
            likesCount: comment.total_likes || 0,
          };
        });
        setCommentLikes(initialCommentLikes);
      }
    } catch (error) {
      console.error('Error fetching post data:', error);
    }
  };

  const handleToggleLikePost = async () => {
    if (!postId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${config.baseURL}/create-post-like`,
        { post_id: postId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setIsLiked(prev => {
          const newLikeStatus = !prev;
          setLikesCount(prevCount => newLikeStatus ? prevCount + 1 : prevCount - 1);
          return newLikeStatus;
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleToggleBookmarkPost = async () => {
    if (!postId) return;
    try {
      const token = localStorage.getItem('token');
      const mediaArray = media.map((item) => ({
        id: item.id,
        post_id: postId,
        url: item.url,
        file_type: item.file_type,
      }));

      const response = await axios.post(
        `${config.baseURL}/bookmark-post`,
        { 
          post_id: postId, 
          media: mediaArray 
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setIsBookmarked(prev => !prev);
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const handleCommentChange = (event) => {
    const text = event.target.value;
    const cursorPosition = event.target.selectionStart;
    setComment(text);

    const textBeforeCursor = text.slice(0, cursorPosition);
    const atSymbolIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atSymbolIndex !== -1) {
      const searchQuery = textBeforeCursor.slice(atSymbolIndex + 1).toLowerCase();
      if (searchQuery) {
        const filteredUsers = allUsers.filter(user => 
          user.user_name.toLowerCase().startsWith(searchQuery) ||
          user.name.toLowerCase().startsWith(searchQuery)
        );
        setMentionList(filteredUsers);
        setShowMentionList(true);
      } else {
        setShowMentionList(false);
      }
    } else {
      setShowMentionList(false);
    }
  };

  const handleMentionClick = (user) => {
    setMentions((prevMentions) => [...prevMentions, user.id]);
    setShowMentionList(false);

    const textarea = textareaRef.current;
    const cursorPosition = textarea.selectionStart;
    const textBeforeCursor = comment.slice(0, cursorPosition);
    const textAfterCursor = comment.slice(cursorPosition);
    
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const newText = textBeforeCursor.slice(0, lastAtIndex) + 
                   `@${user.user_name} ` + 
                   textAfterCursor;
    
    setComment(newText);
    
    const newCursorPosition = lastAtIndex + user.user_name.length + 2;
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleEmojiClick = (emoji) => {
    setComment(prevText => prevText + emoji);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('post_id', postId);
      formData.append('comment_text', comment);

      if (replyToCommentId) {
        formData.append('comment_id', replyToCommentId);
      }

      mentions.forEach((mentionId, i) => {
        formData.append(`mentions[${i}]`, mentionId);
      });

      const response = await axios.post(`${config.baseURL}/create-post-comment`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.data.success) {
        setComment('');
        setReplyToUser(null);
        setReplyToCommentId(null);
        setShowEmojiPicker(false);
        setMentions([]);
        fetchPostData();
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReplyClick = (userName, commentId) => {
    setReplyToUser(userName);
    setReplyToCommentId(commentId);
    setComment(`@${userName} `);
  };

  const handleToggleCommentLike = async (commentId) => {
    if (!commentId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${config.baseURL}/like-post-comment`,
        new URLSearchParams({ comment_id: commentId }),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      if (response.status === 200) {
        setCommentLikes(prevLikes => {
          const updatedLikes = { ...prevLikes };
          const isLiked = !prevLikes[commentId].isLiked;
          updatedLikes[commentId] = {
            isLiked,
            likesCount: prevLikes[commentId].likesCount + (isLiked ? 1 : -1),
          };
          return updatedLikes;
        });
      }
    } catch (error) {
      console.error('Error toggling comment like:', error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!commentId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${config.baseURL}/post-comment-delete`,
        new URLSearchParams({ comment_id: commentId }),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      if (response.status === 200) {
        fetchPostData();
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const getInitials = (fullName) => {
    return fullName.split(' ').map(part => part.charAt(0)).join('');
  };

  const renderMentionList = () => {
    return (
      showMentionList && mentionList.length > 0 && (
        <div className="list_arrow_box">
          <ul className="mention-list">
            {mentionList.map((user, index) => (
              <li key={index} onClick={() => handleMentionClick(user)}>
                <div className="imagebox">
                  {user.profile ? (
                    <img src={user.profile} alt={user.user_name} />
                  ) : (
                    <div className="name_box_mentions">
                      {user.name.split(' ').map(n => n[0]).join('')}
                    </div>
                  )}
                </div>
                {/* <span className="mention-username">{user.user_name}</span> */}
                <span className="mention-name">{user.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )
    );
  };

  const toggleCommentsVisibility = () => {
    setCommentsVisible(!commentsVisible);
  };

  return (
    <>
      <div className="post_meta_likes">
        <div className="connect_box">
          <div className="post_meta_likes_left">
            <div className={`buttoms_meta ${!isSubscribed ? 'disabled' : ''}`}>
              <Link 
                onClick={handleToggleLikePost}
                className={isSubscribed ? '' : 'disabled-link'}
              >
                <img
                  src={isLiked ? heart_icon_fill : heart_icon}
                  alt="Like"
                />
              </Link>
              <Link 
                className={`comments_block ${isSubscribed ? '' : 'disabled-link'}`}
                onClick={isSubscribed ? toggleCommentsVisibility : undefined}
              >
                <img src={comment_icon} alt="Comment" />
              </Link>
              <div 
                className={`send_tip ${isSubscribed ? '' : 'disabled-link'}`}
              >
                {post && !post.is_auth_user_post && (
                  <TipButton 
                    imageSrc={tip_icon} 
                    altText="Send Tip" 
                    text="Send Tip"
                    postId={postId}
                    userId={userId}
                    user={post.user}
                    walletBalance={walletBalance}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="post_meta_likes_right">
          <Link onClick={handleToggleBookmarkPost}>
              <img
                src={isBookmarked ? bookmark_icon_fill : bookmark_icon}
                alt="Bookmark"
              />
            </Link>
          </div>
        </div>
        <div className="like_comment_count">
          <div className="data_show_likes_comment">
            <span>{likesCount} </span> Likes
          </div>
          <div className="data_show_likes_comment">
            <span>{commentsCount} </span> comments
          </div>
        </div>
      </div>

      {commentsVisible && (
        <div className="comment_section">
          {displayedComments.map(comment => (
            <div key={comment.id} className="comment_list">
              <div className="comment_user_profile">
                {comment.user.user_details?.profile ? (
                  <div className="imageavailable">
                    <Link to={`/${comment.user.user_name}`}>
                      <img src={comment.user.user_details.profile} alt={comment.user.name} />
                    </Link>
                  </div>
                ) : (
                  <div className="image_not_available">
                    <Link to={`/${comment.user.user_name}`}>{getInitials(comment.user.name)}</Link>
                  </div>
                )}
              </div>
              <div className="comment_text_meta">
                <div className="comment_username">
                  <Link className="username_comment_verified" to={`/${comment.user.user_name}`}>
                    {comment.user.name} <img src={verified} alt="Verified" />
                  </Link>
                  <div className="comment_text">
                    {comment.content.split(' ').map((word, index) =>
                      word.startsWith('@') ? (
                        <Link
                          key={index}
                          to={`/${word.slice(1)}`}
                          className="mention_link"
                        >
                          {word}{' '}
                        </Link>
                      ) : (
                        <span key={index}>{word} </span>
                      )
                    )}
                  </div>
                </div>
                <div className="comment_meta">
                  <div className="comment_time">
                    {comment.created_at_human}
                  </div>
                  {commentLikes[comment.id]?.likesCount > 0 && (
                    <div className="comment_like_count">
                      {commentLikes[comment.id]?.likesCount} {commentLikes[comment.id]?.likesCount === 1 ? 'Like' : 'Likes'}
                    </div>
                  )}
                  <div className="reply_link">
                    <Link onClick={() => handleReplyClick(comment.user.user_name, comment.id)}>Reply</Link>
                  </div>
                  {comment.is_auth_user_comment && (
                    <div className="head_dropdown post_dropdown">
                      <ul>
                        <li className="nav-item dropdown">
                          <a
                            className="nav-link dropdown-toggle"
                            href="#"
                            id="productsDropdown"
                            role="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <img className="comment_dropdown" src={drop_horz} alt="Dropdown icon" />
                          </a>
                          <ul className="dropdown-menu" aria-labelledby="productsDropdown">
                            <li className="dropdown">
                              <Link
                                className="dropdown-item danger_link"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleDeleteComment(comment.id);
                                }}
                              >
                                Delete
                              </Link>
                            </li>
                          </ul>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div className="comment_like">
                <img
                  src={commentLikes[comment.id]?.isLiked ? heart_icon_fill : heart_icon}
                  alt="Like"
                  onClick={() => handleToggleCommentLike(comment.id)}
                />
              </div>
            </div>
          ))}

          {comments.length > 3 && !showMore && (
            <div className="show_more_comments">
              <Link to={`/${post?.id}/${post?.user.user_name}`}>
                Show more comments
              </Link>
            </div>
          )}

          <div className="comment_input_box">
            <div className="comment_emoji">
              {commonEmojis.map(emoji => (
                <button
                  key={emoji}
                  onClick={() => handleEmojiClick(emoji)}
                  className="emoji_button"
                >
                  {emoji}
                </button>
              ))}
            </div>
            <div className="comment_input_block">
              <div className="input_block_user">
                {userDetails?.user_profile ? (
                  <div className="imageavailable">
                    <Link to="#">
                      <img src={userDetails.user_profile} alt="User" />
                    </Link>
                  </div>
                ) : (
                  <div className="image_not_available">
                    {getInitials(userName)}
                  </div>
                )}
              </div>
             <div className="input_block_comment">
                  <form onSubmit={handleSubmit}>
                    <div className="comment-textarea-wrapper">
                      <textarea
                        ref={textareaRef}
                        placeholder="Add a comment..."
                        value={comment}
                        onChange={handleCommentChange}
                        maxLength={200}
                        className="comment-textarea"
                      />
                      {renderMentionList()}
                    </div>
                    <button 
                      type="submit" 
                      className="comment_submit" 
                      disabled={isSubmitting}
                    >
                      <img src={commentsend} alt="Send Comment" />
                    </button>
                  </form>
                </div>
            </div>
          </div>

          <div className="comment_hide">
            <Link onClick={toggleCommentsVisibility}>
              Hide comments
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

PostMeta.propTypes = {
  heart_icon: PropTypes.string.isRequired,
  heart_icon_fill: PropTypes.string.isRequired,
  comment_icon: PropTypes.string.isRequired,
  tip_icon: PropTypes.string.isRequired,
  bookmark_icon: PropTypes.string.isRequired,
  bookmark_icon_fill: PropTypes.string.isRequired,
  postId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  walletBalance: PropTypes.number,
};

export default PostMeta;